import { Alert, AlertIcon, Box, Button, Heading, HStack, Icon, Link, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  FiAward,
  FiBriefcase,
  FiCheckCircle,
  FiCheckSquare,
  FiDollarSign,
  FiLock,
  FiMessageSquare,
} from "react-icons/fi";
import {
  BidV2Status,
  Currency,
  useGetProductGroupsAsSupplierQuery,
  useWithdrawBidV2Mutation,
  type EventValueDto,
  type MinimumRequirementDto,
  type PortalSourcingEventDetailDto,
  type BidV2Dto,
  type SelectionCriterionDto,
} from "../../../../../autogen/bff-api";
import { productBidTotal } from "../../../../../common/products/productBidTotal";
import { useApiError } from "../../../../../common/errors/useApiError";
import { displayDateWithTime } from "../../../../../common/formatting/displayDateWithTime";
import { momentLocaleFor } from "../../../../../common/formatting/i18nFormatters";
import { useDisplayDateWithTime } from "../../../../../common/formatting/useDisplayDateWithTime";
import { TipTap, isTiptapContentEmpty } from "../../../../../common/input/TipTap/TipTap";
import { composeCriterionKey } from "../../../../../common/qualification/criterionKey";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { usePortalEventUrls } from "../../usePortalEventUrls";
import { CriterionTypeBadge } from "../../../bid-readonly/CriterionTypeBadge";
import { ReadonlyAnswerCard } from "../../../bid-readonly/ReadonlyAnswerCard";
import { AwardCriteriaAnswers } from "../../../bid-readonly/AwardCriteriaAnswers";
import { DeclarationsTab } from "../../../../v2/sourcing-event/bid-detail/DeclarationsTab";
import { DocumentsTab } from "../../../../v2/sourcing-event/bid-detail/DocumentsTab";
import { EspdTab } from "../../../../v2/sourcing-event/bid-detail/EspdTab";
import { PortalLayout } from "../../components/PortalLayout";
import { formatEstimatedValue } from "../../utils/displayFormatters";
import { LabelValueGrid } from "../../view-single/LabelValueGrid";
import { SectionCard } from "../../view-single/SectionCard";
import { isBidLockedByDeadline } from "./readonlyBidStatus";

interface Props {
  eventId: string;
  bidId: string;
  bid: BidV2Dto;
  event: PortalSourcingEventDetailDto;
}

// Read-only single-page view of a delivered offer. Rendered by EditBidV2Page instead of the
// wizard once the bid reaches a terminal status. Shares its answer/ESPD/document rendering with the
// buyer bid-detail view so a design change lands in one place.
export const BidV2ReadonlyView = ({ eventId, bidId, bid, event }: Props) => {
  const { t } = useTranslation();

  // Delivered where the supplier was admitted rather than with this offer, so those sections do
  // not belong here.
  const espdTravelsWithBid = event.collectsEspdWithBid ?? true;
  const collectSupplierPrice = event.collectSupplierPrice ?? true;
  const selectionCriteria = event.selectionCriteria ?? [];
  const minimumRequirements = event.minimumRequirements ?? [];
  const awardCriteria = event.awardCriteria?.criteria ?? [];
  const { data: productData } = useGetProductGroupsAsSupplierQuery({ sourcingEventId: eventId });
  const productGroups = productData?.productGroups ?? [];
  const derivedTotal = productGroups.length > 0 ? productBidTotal(productGroups, bid.products) : null;
  const totalPrice =
    productGroups.length > 0
      ? derivedTotal != null
        ? { value: derivedTotal, currency: bid.price?.currency ?? Currency.Nok }
        : null
      : bid.price;

  return (
    <PortalLayout>
      <Stack spacing="6">
        <ReadonlyBidHeader eventId={eventId} bidId={bidId} bid={bid} event={event} />

        <SupplierIdentificationSection bid={bid} />

        {espdTravelsWithBid && bid.declarations?.hasResponse && (
          <DeclarationsTab declarations={bid.declarations} resetKey={bid.id} />
        )}

        {espdTravelsWithBid && bid.espd?.hasResponse && <EspdTab espd={bid.espd} />}

        {espdTravelsWithBid && selectionCriteria.length > 0 && (
          <QualificationSection criteria={selectionCriteria} bid={bid} />
        )}

        {minimumRequirements.length > 0 && <MinimumRequirementsSection requirements={minimumRequirements} bid={bid} />}

        {(collectSupplierPrice || productGroups.length > 0) && <PriceSection price={totalPrice} />}

        {awardCriteria.length > 0 && (
          <SectionCard icon={FiAward} title={t("Award criteria")} count={awardCriteria.length}>
            <AwardCriteriaAnswers
              criteria={awardCriteria}
              responses={bid.awardCriterionResponses ?? []}
              price={totalPrice}
              answerLabel={String(t("Your answer"))}
              notAnsweredLabel={String(t("Not answered"))}
            />
          </SectionCard>
        )}

        {!isTiptapContentEmpty(bid.coverLetter) && (
          <SectionCard icon={FiMessageSquare} title={t("Cover letter")}>
            <TipTap content={bid.coverLetter ?? ""} editable={false} hasBorder={false} />
          </SectionCard>
        )}

        <DocumentsTab
          documents={bid.documents ?? []}
          signedDocumentId={bid.signedDocumentId}
          emptyDescription={String(t("You have not attached any documents to this offer."))}
        />
      </Stack>
    </PortalLayout>
  );
};

const ReadonlyBidHeader = ({ eventId, bidId, bid, event }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const portalUrls = usePortalEventUrls();
  const apiError = useApiError();
  const displayDateTime = useDisplayDateWithTime();
  const [withdrawBid, { isLoading: isWithdrawing }] = useWithdrawBidV2Mutation();
  const editConfirmation = useConfirmationModal();

  const submittedTime = bid.submittedAt ? displayDateTime(bid.submittedAt) : null;
  const lockedDraft = isBidLockedByDeadline(bid);
  const deadlineTime = bid.submissionDeadline
    ? displayDateWithTime(bid.submissionDeadline, null, momentLocaleFor(i18n.language))
    : null;
  // A delivered offer stays editable until the deadline: editing withdraws it and reopens the wizard.
  // Off the bid, not the event: a revised tender is governed by its negotiation round's frist.
  const canEdit = bid.status === BidV2Status.Submitted && bid.submissionDeadlinePassed !== true;

  const confirmEdit = async () => {
    try {
      await withdrawBid({ bidId }).unwrap();
      const intro = portalUrls.bid.edit.go(eventId, bidId).intro;
      navigate({ pathname: intro.pathname, search: intro.search });
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const openEditConfirmation = () =>
    editConfirmation.show({
      title: t("Edit offer?"),
      description: t(
        "The delivered offer will be withdrawn, and you must deliver it again after you finish making changes. Do you still want to edit the offer?",
      ),
      confirmButtonText: t("Yes, withdraw the offer to edit it") ?? "",
      confirmButtonColor: "teal",
      size: "lg",
      onConfirmed: confirmEdit,
    });

  return (
    <Box>
      <Link
        as={RouterLink}
        to={portalUrls.view.go(eventId)}
        color="smPrimary"
        fontSize="sm"
        fontWeight="semibold"
      >
        ← {t("Back to the procurement")}
      </Link>
      <Heading size="lg" mt="2">
        {t("Your offer")}
        {event.title && (
          <Text as="span" color="smMuted" fontWeight="normal">
            {" – "}
            {event.title}
          </Text>
        )}
      </Heading>

      <Stack
        mt="4"
        spacing="3"
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="lg"
        bg="smBackground"
        boxShadow="sm"
        p="4"
      >
        <HStack spacing="2" align="center">
          <Icon as={lockedDraft ? FiLock : FiCheckSquare} color={lockedDraft ? "smMuted" : "smPrimary"} boxSize="5" />
          <Text fontWeight="semibold" color="smText">
            {lockedDraft
              ? t("Not delivered")
              : submittedTime
                ? t("Delivered {{time}}", { time: submittedTime })
                : t("Offer delivered")}
          </Text>
        </HStack>
        <Text fontSize="sm" color="smMuted">
          {t("Reference: {{reference}}", { reference: bid.id })}
        </Text>
        {!lockedDraft && (
          <Text fontSize="sm" color="smMuted">
            {t("A receipt has been sent by email.")}
          </Text>
        )}
        {lockedDraft && deadlineTime && (
          <Alert status="warning" borderRadius="md" fontSize="sm">
            <AlertIcon />
            {t("The deadline passed {{time}}. The offer can no longer be changed or delivered.", {
              time: deadlineTime,
            })}
          </Alert>
        )}
        {canEdit ? (
          <Box>
            <Button colorScheme="teal" size="sm" onClick={openEditConfirmation} isLoading={isWithdrawing}>
              {t("Edit offer")}
            </Button>
          </Box>
        ) : (
          bid.status === BidV2Status.Submitted && (
            <Text fontSize="sm" color="smMuted">
              {t("The submission deadline has passed and the offer can no longer be edited.")}
            </Text>
          )
        )}
      </Stack>
      {editConfirmation.modal}
    </Box>
  );
};

const SupplierIdentificationSection = ({ bid }: { bid: BidV2Dto }) => {
  const { t } = useTranslation();
  const eo = bid.espdResponse?.economicOperator;
  const rep = eo?.representatives?.[0];

  // Sourced from the bid (the owning org resolved by the BFF), not the auth-session org, so the
  // read-only view is fed from one coherent source alongside the submitted representative.
  const rows = [
    ...(bid.ownerName ? [{ label: t("Supplier"), value: bid.ownerName }] : []),
    ...(bid.ownerOrganizationNumber
      ? [{ label: t("Organization number"), value: bid.ownerOrganizationNumber }]
      : []),
    ...(rep
      ? [
          {
            label: t("Authorized representative"),
            value: [rep.firstName, rep.familyName].filter(Boolean).join(" ") || t("Not provided"),
          },
        ]
      : []),
    ...(rep?.email ? [{ label: t("Email"), value: rep.email }] : []),
    ...(rep?.role ? [{ label: t("Role"), value: rep.role }] : []),
    ...(eo?.smeIndicator != null
      ? [{ label: t("Small or medium-sized enterprise (SME)"), value: eo.smeIndicator ? t("Yes") : t("No") }]
      : []),
  ];

  return (
    <SectionCard icon={FiBriefcase} title={t("About the supplier")}>
      <LabelValueGrid rows={rows} />
    </SectionCard>
  );
};

const QualificationSection = ({
  criteria,
  bid,
}: {
  criteria: SelectionCriterionDto[];
  bid: BidV2Dto;
}) => {
  const { t } = useTranslation();
  const responsesByKey = new Map((bid.qualificationResponses ?? []).map((r) => [r.criterionKey, r] as const));

  return (
    <SectionCard icon={FiAward} title={t("Qualification requirements")} count={criteria.length}>
      <Stack spacing="4">
        {criteria.map((criterion) => {
          const response = responsesByKey.get(composeCriterionKey(criterion));
          return (
            <ReadonlyAnswerCard
              key={composeCriterionKey(criterion)}
              badge={<CriterionTypeBadge type={criterion.type} />}
              title={criterion.name}
              descriptionLabel={String(t("Documentation requirement"))}
              descriptionHtml={criterion.description ?? null}
              answerLabel={String(t("Your answer"))}
              answer={response?.description ? { kind: "text", value: response.description } : { kind: "empty" }}
              notAnsweredLabel={String(t("Not answered"))}
              evidence={(response?.evidenceDocuments ?? []).map((doc) => ({ id: doc.id, name: doc.name }))}
            />
          );
        })}
      </Stack>
    </SectionCard>
  );
};

const MinimumRequirementsSection = ({
  requirements,
  bid,
}: {
  requirements: MinimumRequirementDto[];
  bid: BidV2Dto;
}) => {
  const { t } = useTranslation();
  const responsesById = new Map(
    (bid.minimumRequirementResponses ?? []).map((r) => [r.minimumRequirementId, r] as const),
  );

  return (
    <SectionCard icon={FiCheckCircle} title={t("Minimum requirements")} count={requirements.length}>
      <Stack spacing="4">
        {requirements.map((requirement) => {
          const response = requirement.id ? responsesById.get(requirement.id) : undefined;
          return (
            <ReadonlyAnswerCard
              key={requirement.id ?? requirement.title}
              title={requirement.title}
              descriptionHtml={requirement.description ?? null}
              answerLabel={String(t("Your answer"))}
              answer={
                response ? { kind: "boolean", value: response.answer, comment: response.comment } : { kind: "empty" }
              }
              notAnsweredLabel={String(t("Not answered"))}
            />
          );
        })}
      </Stack>
    </SectionCard>
  );
};

const PriceSection = ({ price }: { price: EventValueDto | null | undefined }) => {
  const { t } = useTranslation();
  const value =
    price?.value != null ? formatEstimatedValue(price.value, price.currency) : String(t("No price provided"));

  return (
    <SectionCard icon={FiDollarSign} title={t("Price")}>
      <LabelValueGrid rows={[{ label: t("Total price"), value }]} />
    </SectionCard>
  );
};
